import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { WorkGroupBrowserToolbar } from '@/features/workgroups/components/work-group-browser-toolbar';
import { WorkGroupContentsList } from '@/features/workgroups/components/work-group-contents-list';
import { PrivateAccessReasonDialog } from '@/features/workgroups/components/private-access-reason-dialog';
import { DeleteFolderDialog } from '@/features/workgroups/components/delete-folder-dialog';
import { RenameFolderDialog } from '@/features/workgroups/components/rename-folder-dialog';
import { WorkGroupFolderPermissionsDialog } from '@/features/workgroups/components/work-group-folder-permissions-dialog';
import { WorkGroupPrivateAccessBanner } from '@/features/workgroups/components/work-group-private-access-banner';
import {
  useWorkGroup,
  useWorkGroupContents,
  useWorkgroupsPrivateAccess,
} from '@/hooks/use-workgroups';
import {
  useWorkgroupsUIStore,
  WORKGROUPS_PRIVATE_REASON_MIN_LEN,
} from '@/stores/workgroups-ui-store';

export function WorkGroupsFolderPage() {
  const { workGroupId, folderId } = useParams<{ workGroupId: string; folderId: string }>();
  const setSelectedWorkGroupId = useWorkgroupsUIStore((s) => s.setSelectedWorkGroupId);
  const setSelectedFolderId = useWorkgroupsUIStore((s) => s.setSelectedFolderId);

  const privateAccessReason = useWorkgroupsUIStore((s) => {
    if (!workGroupId) return undefined;
    const trimmed = s.privateAccessByWorkGroupId[workGroupId]?.trim() ?? '';
    return trimmed.length >= WORKGROUPS_PRIVATE_REASON_MIN_LEN ? trimmed : undefined;
  });

  const { data: wg, isLoading: wgLoading, error: wgError } = useWorkGroup(
    workGroupId,
    privateAccessReason,
  );
  const access = useWorkgroupsPrivateAccess(workGroupId, wg);

  const forbiddenNeedsReason =
    axios.isAxiosError(wgError) &&
    wgError.response?.status === 403 &&
    access.canPrivateAdmin &&
    !privateAccessReason;

  const blockContent = access.reasonMissing || forbiddenNeedsReason;

  const { data: contents, isLoading: contentsLoading, error: contentsError } = useWorkGroupContents(
    workGroupId,
    folderId,
    undefined,
    privateAccessReason,
    !!workGroupId && !!folderId && !blockContent,
  );

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [permissionsFolder, setPermissionsFolder] = useState<{ id: string; name: string } | null>(
    null,
  );

  useEffect(() => {
    if (workGroupId) {
      setSelectedWorkGroupId(workGroupId);
    }
    if (folderId) {
      setSelectedFolderId(folderId);
    }
    return () => {
      setSelectedWorkGroupId(null);
      setSelectedFolderId(null);
    };
  }, [workGroupId, folderId, setSelectedWorkGroupId, setSelectedFolderId]);

  if (!workGroupId || !folderId) {
    return <p className="text-sm text-muted-foreground">Missing folder.</p>;
  }

  const folderTitle = contents?.folder?.name ?? 'Folder';
  const isRoot = contents?.folder?.isRoot ?? false;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PrivateAccessReasonDialog
        open={access.promptOpen || forbiddenNeedsReason}
        onSubmit={access.submitReason}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/workgroups/${workGroupId}`}>← Up to work group</Link>
        </Button>
      </div>
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{folderTitle}</h1>
        <p className="text-sm text-muted-foreground">
          {wg?.name?.trim() || 'Work group'} · {wgLoading ? 'Loading…' : wgError ? 'Error' : wg?.type}
        </p>
      </div>

      {access.needsReason && privateAccessReason ? (
        <WorkGroupPrivateAccessBanner
          reason={privateAccessReason}
          onChangeReason={() => access.clearReason()}
        />
      ) : null}

      {forbiddenNeedsReason ? (
        <p className="text-sm text-muted-foreground">
          Administrator access to this private work group requires a reason.
        </p>
      ) : null}

      {!blockContent ? (
        <>
          <WorkGroupBrowserToolbar
            workGroupId={workGroupId}
            currentFolderId={folderId}
            isRootFolder={isRoot}
            privateAccessReason={privateAccessReason}
            onRenameCurrentFolder={() => setRenameOpen(true)}
            onDeleteCurrentFolder={() => setDeleteOpen(true)}
            onManagePermissions={() => {
              if (contents?.folder) {
                setPermissionsFolder({ id: contents.folder.id, name: contents.folder.name });
              }
            }}
          />
          {contentsLoading ? <p className="text-sm text-muted-foreground">Loading contents…</p> : null}
          {contentsError ? (
            <p className="text-sm text-destructive">Could not load folder contents.</p>
          ) : null}
          {contents ? (
            <WorkGroupContentsList
              workGroupId={workGroupId}
              data={contents}
              privateAccessReason={privateAccessReason}
              onManageFolderPermissions={(folder) =>
                setPermissionsFolder({ id: folder.id, name: folder.name })
              }
            />
          ) : null}
          {permissionsFolder ? (
            <WorkGroupFolderPermissionsDialog
              workGroupId={workGroupId}
              folderId={permissionsFolder.id}
              folderName={permissionsFolder.name}
              privateAccessReason={privateAccessReason}
              open={!!permissionsFolder}
              onOpenChange={(open) => {
                if (!open) setPermissionsFolder(null);
              }}
            />
          ) : null}
        </>
      ) : null}

      {contents?.folder && !isRoot ? (
        <>
          <RenameFolderDialog
            workGroupId={workGroupId}
            folderId={folderId}
            folderName={contents.folder.name}
            parentId={contents.folder.parentId}
            privateAccessReason={privateAccessReason}
            open={renameOpen}
            onOpenChange={setRenameOpen}
          />
          <DeleteFolderDialog
            workGroupId={workGroupId}
            folderId={folderId}
            folderName={contents.folder.name}
            parentId={contents.folder.parentId}
            privateAccessReason={privateAccessReason}
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
          />
        </>
      ) : null}
    </div>
  );
}
